import { useState } from "react";
import axios from "axios";
import { Send, MessageCircle, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_RAW = "+40746264632";
const PHONE_DISPLAY = "+40 746 264 632";
const WHATSAPP_BASE = "https://wa.me/40746264632";

const EMPTY_FORM = {
  name: "",
  email: "",
  preferred_date: "",
  group_size: "",
  dietary_notes: "",
  message: "",
};

// --- Pure helpers (kept outside the component to reduce its complexity) ---

function buildWhatsAppUrl(data) {
  const lines = [
    "Hello Andrea — I'd love to request seats at your table.",
    "",
    `Name: ${data.name}`,
    `Email: ${data.email}`,
    data.preferred_date ? `Preferred date: ${data.preferred_date}` : null,
    data.group_size ? `Group size: ${data.group_size}` : null,
    data.dietary_notes ? `Dietary notes: ${data.dietary_notes}` : null,
    data.message ? `\n${data.message}` : null,
  ].filter(Boolean);
  return `${WHATSAPP_BASE}?text=${encodeURIComponent(lines.join("\n"))}`;
}

function validate(form) {
  if (!form.name || !form.email) {
    return "Please share your name and email.";
  }
  return null;
}

async function submitInquiry(form, popup) {
  await axios.post(`${API}/inquiries`, form);
  const url = buildWhatsAppUrl(form);
  if (popup) popup.location.href = url;
  else window.location.href = url;
}

export default function InquiryForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ state: "idle", text: "" });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validate(form);
    if (error) {
      setStatus({ state: "error", text: error });
      return;
    }
    setStatus({ state: "loading", text: "Sending…" });
    // Open popup synchronously inside the user gesture so it isn't blocked.
    const popup = window.open("about:blank", "_blank", "noopener,noreferrer");
    try {
      await submitInquiry(form, popup);
      setStatus({
        state: "success",
        text: "Thank you. Opening WhatsApp so we can continue the conversation…",
      });
    } catch {
      if (popup) popup.close();
      setStatus({
        state: "error",
        text: "Something went amiss. Please try WhatsApp or a call directly.",
      });
    }
  };

  return (
    <section
      id="inquire"
      className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-24 sm:py-32"
      data-testid="inquiry-section"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left column: framing */}
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">
            <span className="text-[color:var(--gold)]">06 ·</span> Request seats
          </p>
          <span className="rule" />
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight text-[color:var(--ink)]">
            Write to me<br />
            <em className="font-display italic text-[color:var(--moss)]">like a friend.</em>
          </h2>
          <p className="font-body text-[1.05rem] leading-relaxed text-[color:var(--ink-2)] mt-8 max-w-md">
            Every gathering is hand-arranged. Tell me a little about you, the days
            that might suit and any allergies. I'll write back personally — usually
            within a day.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={WHATSAPP_BASE}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              data-testid="whatsapp-link-button"
            >
              <MessageCircle size={16} strokeWidth={1.5} />
              <span>WhatsApp Andrea</span>
            </a>
            <div>
              <a
                href={`tel:${PHONE_RAW}`}
                className="inline-block font-display italic text-lg text-[color:var(--ink)] underline decoration-[color:var(--gold)] decoration-2 underline-offset-[6px] hover:text-[color:var(--moss)] transition-colors"
                data-testid="call-link-button"
              >
                <Phone size={14} strokeWidth={1.5} className="inline mr-2 -mt-1" />
                or call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </Reveal>

        {/* Right column: the letter form */}
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form
            onSubmit={onSubmit}
            className="bg-[color:var(--paper-2)]/60 backdrop-blur-sm p-8 sm:p-12 border border-[color:var(--rule)]"
            data-testid="inquiry-form"
          >
            <p className="font-hand text-2xl text-[color:var(--moss)] mb-2">Dear Andrea,</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 mt-6">
              <div>
                <label className="letter-label" htmlFor="name">Your name</label>
                <input
                  id="name"
                  type="text"
                  className="letter-input"
                  placeholder="e.g. Mara Kovács"
                  value={form.name}
                  onChange={handle("name")}
                  data-testid="inquiry-name-input"
                />
              </div>
              <div>
                <label className="letter-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="letter-input"
                  placeholder="you@somewhere.com"
                  value={form.email}
                  onChange={handle("email")}
                  data-testid="inquiry-email-input"
                />
              </div>
              <div>
                <label className="letter-label" htmlFor="date">A day that might suit</label>
                <input
                  id="date"
                  type="text"
                  className="letter-input"
                  placeholder="A Sunday in May, perhaps…"
                  value={form.preferred_date}
                  onChange={handle("preferred_date")}
                  data-testid="inquiry-date-input"
                />
              </div>
              <div>
                <label className="letter-label" htmlFor="group">How many seats?</label>
                <select
                  id="group"
                  className="letter-input bg-transparent"
                  value={form.group_size}
                  onChange={handle("group_size")}
                  data-testid="inquiry-group-size-select"
                >
                  <option value="">Choose…</option>
                  <option value="2">2 guests</option>
                  <option value="3">3 guests</option>
                  <option value="4">4 guests</option>
                  <option value="5">5 guests</option>
                  <option value="6">6 guests</option>
                  <option value="7">7 guests</option>
                  <option value="8">8 guests</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="letter-label" htmlFor="diet">Allergies or dietary notes</label>
                <input
                  id="diet"
                  type="text"
                  className="letter-input"
                  placeholder="My kitchen is nut-free; do tell me anything else."
                  value={form.dietary_notes}
                  onChange={handle("dietary_notes")}
                  data-testid="inquiry-dietary-input"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="letter-label" htmlFor="msg">A few words from you</label>
                <textarea
                  id="msg"
                  className="letter-input"
                  placeholder="What drew you to The Unfinished Lunch? Anything I should know about you?"
                  value={form.message}
                  onChange={handle("message")}
                  data-testid="inquiry-message-input"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                type="submit"
                className="btn-primary inline-flex items-center gap-3"
                disabled={status.state === "loading"}
                data-testid="inquiry-submit-button"
              >
                <Send size={14} strokeWidth={1.5} />
                <span>{status.state === "loading" ? "Sending…" : "Send my note"}</span>
              </button>
              <p className="font-hand text-xl text-[color:var(--ink-2)]">— with warmth</p>
            </div>

            {status.text && (
              <p
                className={`mt-6 text-sm ${
                  status.state === "error"
                    ? "text-[color:#a23b2a]"
                    : "text-[color:var(--moss)]"
                }`}
                data-testid="inquiry-status"
              >
                {status.text}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
